import React, { useEffect, useRef, useState } from "react";
import { Eraser } from "lucide-react";
import SignaturePadLibrary from "signature_pad";
import YesNoOptions, { isYesNoField } from "./YesNoOptions";

export function SignaturePad({ onChange, compact = false }) {
  const canvasRef = useRef(null);
  const padRef = useRef(null);
  const pixelRatioRef = useRef(1);
  const [hasInk, setHasInk] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const pad = new SignaturePadLibrary(canvas, { minWidth: 0.7, maxWidth: 2.2, penColor: "#111827" });
    padRef.current = pad;
    const handleEndStroke = () => {
      const empty = pad.isEmpty();
      setHasInk(!empty);
      onChange(empty ? "" : pad.toDataURL("image/png"));
    };
    pad.addEventListener("endStroke", handleEndStroke);
    const resizeCanvas = () => {
      const width = canvas.clientWidth;
      const height = canvas.clientHeight;
      if (!width || !height) return;
      const oldWidth = canvas.width / pixelRatioRef.current;
      const oldHeight = canvas.height / pixelRatioRef.current;
      const points = pad.toData();
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      canvas.width = width * ratio;
      canvas.height = height * ratio;
      canvas.getContext("2d").scale(ratio, ratio);
      pixelRatioRef.current = ratio;
      pad.clear();
      if (points.length && oldWidth && oldHeight) {
        pad.fromData(points.map((group) => ({
          ...group,
          points: group.points.map((point) => ({
            ...point, x: point.x * width / oldWidth, y: point.y * height / oldHeight,
          })),
        })));
      }
      const empty = pad.isEmpty();
      setHasInk(!empty);
      onChange(empty ? "" : pad.toDataURL("image/png"));
    };
    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);
    window.addEventListener("orientationchange", resizeCanvas);
    return () => {
      window.removeEventListener("resize", resizeCanvas);
      window.removeEventListener("orientationchange", resizeCanvas);
      pad.removeEventListener("endStroke", handleEndStroke);
      pad.off();
      padRef.current = null;
    };
  }, [onChange]);
  const clear = () => {
    padRef.current?.clear();
    setHasInk(false);
    onChange("");
  };
  return <div className="space-y-2">
    <div className="overflow-hidden border border-slate-300 bg-white">
      <canvas ref={canvasRef} width="720" height="180" aria-label="Draw your signature" className={`block ${compact ? "h-24" : "h-36"} w-full touch-none`} style={{ touchAction: "none" }} />
    </div>
    <div className="flex items-center justify-between text-xs text-slate-500"><span>Sign inside the area above.</span><button type="button" onClick={clear} disabled={!hasInk} className="inline-flex items-center gap-1 rounded px-2 py-1 font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-40"><Eraser size={14} /> Clear</button></div>
  </div>;
}

export function FormField({ field, value, onChange, compact = false }) {
  if (isYesNoField(field)) return <YesNoOptions field={field} value={value} onChange={onChange}
    disabled={!field.patient_editable} namePrefix="field" />;
  const common = { id: `field-${field.key}`, required: field.required, disabled: !field.patient_editable, value: value ?? "", onChange: (event) => onChange(field.key, field.type === "number" ? (event.target.value === "" ? "" : Number(event.target.value)) : event.target.value) };
  const controlClass = `${compact ? "rounded px-2 py-1 text-[13px] leading-[18px]" : "mt-1 px-3 py-2.5 text-sm"} w-full border border-slate-300 bg-white text-slate-900 focus:border-cyan-800 focus:outline-none focus:ring-2 focus:ring-cyan-100 disabled:bg-slate-100 disabled:text-slate-600`;
  let control;
  if (field.type === "textarea") control = <textarea {...common} rows={compact ? 2 : 4} maxLength={field.validation?.max_length} minLength={field.validation?.min_length} className={controlClass} />;
  else if (field.type === "checkbox") control = <input id={common.id} type="checkbox" checked={Boolean(value)} required={field.required} disabled={!field.patient_editable} onChange={(event) => onChange(field.key, event.target.checked)} className="h-4 w-4 rounded border-slate-300 text-cyan-800 focus:ring-cyan-700 disabled:opacity-60" />;
  else if (field.type === "select") control = <select {...common} className={controlClass}><option value="">Choose an option</option>{(field.options || []).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select>;
  else control = <input {...common} type={field.type === "number" ? "number" : field.type === "phone" ? "tel" : ["date", "email"].includes(field.type) ? field.type : "text"} min={field.validation?.minimum} max={field.validation?.maximum} minLength={field.validation?.min_length} maxLength={field.validation?.max_length} pattern={field.validation?.pattern || (field.type === "phone" ? "\\+?[0-9]{8,15}" : undefined)} className={controlClass} />;
  return <div className={compact ? "registration-row" : "py-1"}><label htmlFor={common.id} className={`block text-sm ${compact ? "font-medium text-slate-700" : "font-semibold text-slate-800"}`}>{field.label}{field.required && <span className="ml-1 text-rose-700" aria-label="required">*</span>}</label><div className={!compact && field.type === "checkbox" ? "mt-2" : ""}>{control}</div></div>;
}
